import { useQuery } from '@tanstack/react-query';
import { MapPin, Factory, Layers } from 'lucide-react';
import { fetchFactoryAreas, fetchProductionLinesByArea, fetchStationsByLine } from '@/core/api';
import { useFactory } from '@/core/hooks/useFactory';
import { useTenant } from '@/core/hooks/useTenant';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import { Label } from '@/shared/ui/label';

interface HierarchySelectorProps {
  areaId: string | null;
  lineId: string | null;
  stationId: string | null;
  onAreaChange: (areaId: string | null) => void;
  onLineChange: (lineId: string | null) => void;
  onStationChange: (stationId: string | null) => void;
  /**
   * Whether to show the station selector
   */
  includeStation?: boolean;
  /**
   * Whether to allow clearing selections
   */
  allowClear?: boolean;
  /**
   * Disable all selectors
   */
  disabled?: boolean;
  /**
   * Optional className for the container
   */
  className?: string;
  /**
   * Show labels above selectors
   */
  showLabels?: boolean;
}

/**
 * Reusable hierarchy selector (Area → Line → Station).
 * Cascading selectors that automatically filter based on parent selection.
 */
export const HierarchySelector = ({
  areaId,
  lineId,
  stationId,
  onAreaChange,
  onLineChange,
  onStationChange,
  includeStation = false,
  allowClear = false,
  disabled = false,
  className,
  showLabels = true,
}: HierarchySelectorProps) => {
  const { selectedFactoryId } = useFactory();
  const { selectedTenantId } = useTenant();

  // Fetch areas
  const { data: areas = [], isLoading: areasLoading } = useQuery({
    queryKey: ['factory-areas', selectedTenantId, selectedFactoryId],
    queryFn: () => {
      if (!selectedFactoryId) return [];
      return fetchFactoryAreas(selectedFactoryId);
    },
    enabled: !!selectedFactoryId,
  });

  // Fetch lines for selected area
  const { data: lines = [], isLoading: linesLoading } = useQuery({
    queryKey: ['production-lines-by-area', selectedTenantId, areaId],
    queryFn: () => {
      if (!areaId) return [];
      return fetchProductionLinesByArea(areaId);
    },
    enabled: !!areaId,
  });

  // Fetch stations for selected line
  const { data: stations = [], isLoading: stationsLoading } = useQuery({
    queryKey: ['stations-by-line', selectedTenantId, lineId],
    queryFn: () => {
      if (!lineId) return [];
      return fetchStationsByLine(lineId);
    },
    enabled: !!lineId && includeStation,
  });

  if (!selectedFactoryId) {
    return (
      <Badge variant="secondary" className="text-xs">
        Select factory first
      </Badge>
    );
  }

  const handleAreaChange = (value: string) => {
    const newAreaId = value === '__NONE__' ? null : value;
    onAreaChange(newAreaId);
    // Clear downstream selections
    onLineChange(null);
    onStationChange(null);
  };

  const handleLineChange = (value: string) => {
    const newLineId = value === '__NONE__' ? null : value;
    onLineChange(newLineId);
    // Clear downstream selection
    onStationChange(null);
  };

  const handleStationChange = (value: string) => {
    const newStationId = value === '__NONE__' ? null : value;
    onStationChange(newStationId);
  };

  return (
    <div className={`space-y-4 ${className || ''}`}>
      {/* Area Selector */}
      <div className="space-y-2">
        {showLabels && <Label>Area</Label>}
        {areasLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <Select value={areaId || '__NONE__'} onValueChange={handleAreaChange} disabled={disabled}>
            <SelectTrigger>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <SelectValue placeholder="Select area">
                  {areas.find((a) => a.id === areaId)?.name || 'Select area'}
                </SelectValue>
              </div>
            </SelectTrigger>
            <SelectContent>
              {allowClear && (
                <SelectItem value="__NONE__">
                  <span className="text-muted-foreground italic">None</span>
                </SelectItem>
              )}
              {areas.length === 0 ? (
                <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                  No areas available
                </div>
              ) : (
                areas.map((area) => (
                  <SelectItem key={area.id} value={area.id}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: area.color }}
                      />
                      <span>{area.name}</span>
                    </div>
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Line Selector */}
      <div className="space-y-2">
        {showLabels && <Label>Line</Label>}
        {linesLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <Select
            value={lineId || '__NONE__'}
            onValueChange={handleLineChange}
            disabled={disabled || !areaId}
          >
            <SelectTrigger>
              <div className="flex items-center gap-2">
                <Factory className="h-4 w-4 text-muted-foreground" />
                <SelectValue placeholder="Select line">
                  {lines.find((l) => l.id === lineId)?.name || 'Select line'}
                </SelectValue>
              </div>
            </SelectTrigger>
            <SelectContent>
              {allowClear && (
                <SelectItem value="__NONE__">
                  <span className="text-muted-foreground italic">None</span>
                </SelectItem>
              )}
              {!areaId ? (
                <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                  Select an area first
                </div>
              ) : lines.length === 0 ? (
                <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                  No lines in this area
                </div>
              ) : (
                lines.map((line) => (
                  <SelectItem key={line.id} value={line.id}>
                    {line.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Station Selector (optional) */}
      {includeStation && (
        <div className="space-y-2">
          {showLabels && <Label>Station (Optional)</Label>}
          {stationsLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : (
            <Select
              value={stationId || '__NONE__'}
              onValueChange={handleStationChange}
              disabled={disabled || !lineId}
            >
              <SelectTrigger>
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-muted-foreground" />
                  <SelectValue placeholder="Select station">
                    {stations.find((s) => s.id === stationId)?.name || 'Select station'}
                  </SelectValue>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__NONE__">
                  <span className="text-muted-foreground italic">None</span>
                </SelectItem>
                {!lineId ? (
                  <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                    Select a line first
                  </div>
                ) : stations.length === 0 ? (
                  <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                    No stations on this line
                  </div>
                ) : (
                  stations.map((station) => (
                    <SelectItem key={station.id} value={station.id}>
                      {station.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          )}
        </div>
      )}
    </div>
  );
};
